import { useProductStore } from 'stores/product.store';
import { create } from 'zustand';
import { PersistOptions, persist } from 'zustand/middleware';

import { productItem } from 'types/order.type';

import { BOX_DEVICES, CATEGORY, PRODUCT, StorageNames } from '@/lib/constants';

export interface PendingChange {
  code: string;
  quantity: number;
  action: number; // 1 = add, 0 = remove
  occurrenceNumber: string | null;
}

// CommerceTools carts are isolated to the current browser tab.

const clearCompleteTvBundleQueryFlag = (changedProducts: productItem[]) => {
  const hasTvProduct = changedProducts.some(
    (product: any) => product.category?.toLocaleLowerCase() === CATEGORY.TV.toLocaleLowerCase()
  );

  if (hasTvProduct) {
    void useProductStore.getState().setIsCompleteTvBundleFromQuery(false);
  }
};

const dynamicStorage = {
  getItem: (name: string) => {
    const str = sessionStorage.getItem(name);
    if (!str) return null;
    return JSON.parse(str);
  },
  setItem: (name: string, value: any) => {
    sessionStorage.setItem(name, JSON.stringify(value));
  },
  removeItem: (name: string) => {
    sessionStorage.removeItem(name);
  },
};

interface OrderState {
  products: productItem[];
  totalPrice: number;
  subTotalPrice: number;
  otTotalNoOtcPrice: number;
  isPriceUpdate: boolean;
  isLoader: boolean;
  isSaveModelOpen: boolean;
  isSelectedDevice: string;
  isRemoveAllPlan: boolean;
  CTItemsPayload: PendingChange[];
  isHydrated: boolean;
  productsAddedThisSession: boolean;
  setIsRemoveAllPlan: (value: boolean) => void;
  addProductToCart: (product: productItem, productType: string) => Promise<void>;
  addMultipleProductToCart: (products: productItem[], productType: string) => Promise<void>;
  swapBundleProducts: (newProducts: productItem[]) => Promise<void>;
  removeProductFromCart: (productId: number) => Promise<void>;
  removeProductRecordDeviceFromCart: (id: string, title: string) => void;
  removeProductDeviceFromCart: (id: string) => Promise<void>;
  setPrice: (item: any, syncedProducts?: productItem[]) => Promise<void>;
  clearCart: () => Promise<void>;
  clearCTItemsPayload: () => void;
  setIsHydrated: (value: boolean) => void;
  setIsPriceUpdate: (value: boolean) => Promise<void>;
  setIsLoader: (value: boolean) => void;
  setIsSaveModelOpen: (value: boolean) => void;
  setSelectedDevice: (item: any) => Promise<void>;
  getProductBasedOnProductId: (id: number) => Promise<any | undefined>;
  removeBroadbandProduct: () => void;
  removeVoucherSkuList: string[];
  setRemoveVoucherSkuList: (skuList: string[]) => Promise<void>;
  removeVoucherProductFromCartBasedOnSku: (skuList: string[]) => Promise<void>;
}

type PersistOrderState = Pick<OrderState, 'products' | 'CTItemsPayload'>;

const persistOptions: PersistOptions<OrderState, PersistOrderState> = {
  name: StorageNames.ORDER_STORAGE,
  storage: dynamicStorage,
  partialize: (state: OrderState) => ({
    products: state.products,
    totalPrice: state.totalPrice,
    subTotalPrice: state.subTotalPrice,
    isPriceUpdate: state.isPriceUpdate,
    isSelectedDevice: state.isSelectedDevice,
    otTotalNoOtcPrice: state.otTotalNoOtcPrice,
    removeVoucherSkuList: state.removeVoucherSkuList,
    CTItemsPayload: state.CTItemsPayload,
  }),
};

export const useCartStore = create<OrderState>()(
  persist(
    (set, get) => ({
      products: [],
      totalPrice: 0,
      subTotalPrice: 0,
      otTotalNoOtcPrice: 0,
      isPriceUpdate: false,
      isLoader: false,
      isSelectedDevice: '',
      isSaveModelOpen: false,
      removeVoucherSkuList: [],
      isRemoveAllPlan: false,
      CTItemsPayload: [],
      isHydrated: false,
      productsAddedThisSession: false,
      //cartBackup: null,

      addProductToCart: async (product, type) => {
        const existingProducts = get().products || [];
        const alreadyExists = existingProducts.some(
          (p: { productId: any }) => p.productId === product.productId
        );

        if (type === PRODUCT) {
          // prevent duplicates or update quantity
          if (!alreadyExists) {
            const existingCTItemsPayload = get().CTItemsPayload || [];
            set({
              products: [...existingProducts, product],
              productsAddedThisSession: true,
              CTItemsPayload: [
                ...existingCTItemsPayload,
                {
                  code: (product as any).sku,
                  quantity: (product as any).quantity || 1,
                  action: 1,
                  occurrenceNumber: (product as productItem).occurrenceNumber ?? null,
                },
              ],
            });
            clearCompleteTvBundleQueryFlag([product]);
          }
        }

        if (type === BOX_DEVICES) {
          if (!alreadyExists) {
            const existingCTItemsPayload = get().CTItemsPayload || [];
            set({
              products: [...existingProducts, product],
              productsAddedThisSession: true,
              CTItemsPayload: [
                ...existingCTItemsPayload,
                {
                  code: (product as any).sku,
                  quantity: (product as any).quantity || 1,
                  action: 1,
                  occurrenceNumber: (product as any).occurrenceNumber ?? null,
                },
              ],
            });
            clearCompleteTvBundleQueryFlag([product]);
          }
        }
      },

      addMultipleProductToCart: async (products, type) => {
        const existingCTItemsPayload = get().CTItemsPayload || [];
        const newCTItemsPayload = products.map(p => ({
          code: p.sku,
          quantity: p.quantity || 1,
          action: 1,
          occurrenceNumber: p.occurrenceNumber ?? null,
        }));
        set({
          products: [...products],
          productsAddedThisSession: true,
          CTItemsPayload: [...existingCTItemsPayload, ...newCTItemsPayload],
        });
        clearCompleteTvBundleQueryFlag(products);
      },

      swapBundleProducts: async (newProducts: productItem[]) => {
        const existingProducts = get().products || [];
        const existingCTItemsPayload = get().CTItemsPayload || [];

        // First, map occurrenceNumber from existing products
        // to matching new products
        const updatedNewProducts = newProducts.map(newProduct => {
          const existingProduct = existingProducts.find(
            (existingProduct: productItem) => existingProduct.productId === newProduct.productId
          );

          return {
            ...newProduct,
            occurrenceNumber:
              existingProduct?.occurrenceNumber ?? newProduct.occurrenceNumber ?? null,
          };
        });

        const newProductIds = new Set(updatedNewProducts.map(p => p.productId));

        const existingProductIds = new Set(existingProducts.map((p: productItem) => p.productId));

        // Then calculate removes
        const removes = existingProducts
          .filter((p: productItem) => !newProductIds.has(p.productId))
          .map(p => ({
            code: p.sku,
            quantity: p.quantity || 1,
            action: 0,
            occurrenceNumber: p.occurrenceNumber ?? null,
          }));

        // Then calculate adds
        const adds = updatedNewProducts
          .filter(p => !existingProductIds.has(p.productId))
          .map(p => ({
            code: p.sku,
            quantity: p.quantity || 1,
            action: 1,
            occurrenceNumber: p.occurrenceNumber ?? null,
          }));

        set({
          products: [...updatedNewProducts],
          productsAddedThisSession: true,
          CTItemsPayload: [...existingCTItemsPayload, ...removes, ...adds],
          isSelectedDevice: '',
        });

        clearCompleteTvBundleQueryFlag([...existingProducts, ...updatedNewProducts]);
      },

      removeProductFromCart: async productId => {
        const removedProduct = get().products.find(
          (p: { productId: number }) => p.productId === productId
        );
        const filteredProducts = get().products.filter(
          (p: { productId: number }) => p.productId !== productId
        );
        set(state => ({
          products: [...filteredProducts],
          CTItemsPayload: removedProduct
            ? [
                ...state.CTItemsPayload,
                {
                  code: (removedProduct as any).sku,
                  quantity: (removedProduct as any).quantity || 1,
                  action: 0,
                  occurrenceNumber: (removedProduct as any).occurrenceNumber ?? null,
                },
              ]
            : state.CTItemsPayload,
        }));
        if (removedProduct) clearCompleteTvBundleQueryFlag([removedProduct]);
      },

      removeVoucherProductFromCartBasedOnSku: async (skuList: any[]) => {
        const removedProducts = get().products.filter(product => skuList.includes(product.sku));
        const filteredProducts = get().products.filter(product => !skuList.includes(product.sku));
        set({ products: [...filteredProducts], removeVoucherSkuList: [] });
        clearCompleteTvBundleQueryFlag(removedProducts);
      },

      // removeVoucherProductFromCartBasedOnSku: async (skuList: any[]) => {
      //   const filteredProducts = get().products.filter(product => !skuList.includes(product.sku));
      //   set({ products: [...filteredProducts], removeVoucherSkuList: [] });
      // },

      removeProductRecordDeviceFromCart: (id: string, title: string) => {
        const updatedProduct = get().products;
        const actualIndex = updatedProduct.findIndex(
          (p: { occurrenceNumber: string | null }) => p.occurrenceNumber === id
        );

        if (actualIndex !== -1) {
          const removedProduct = updatedProduct[actualIndex];
          updatedProduct.splice(actualIndex, 1);
          set({ products: updatedProduct, isPriceUpdate: false });
          clearCompleteTvBundleQueryFlag([removedProduct]);
        }
      },

      removeProductDeviceFromCart: async (id: string) => {
        const removedProducts = get().products.filter(
          (p: { occurrenceNumber: string | null }) => p.occurrenceNumber === id
        );
        const filteredProducts = get().products.filter(
          (p: { occurrenceNumber: string | null; category: string }) => p.occurrenceNumber !== id
        );
        set(state => ({
          products: [...filteredProducts],
          CTItemsPayload: [
            ...state.CTItemsPayload,
            ...removedProducts.map((p: any) => ({
              code: p.sku,
              quantity: p.quantity || 1,
              action: 0,
              occurrenceNumber: p.occurrenceNumber ?? null,
            })),
          ],
        }));
        clearCompleteTvBundleQueryFlag(removedProducts);
      },
      setPrice: async (item, syncedProducts) => {
        const baseProducts = syncedProducts ?? get().products ?? [];
        const updatedProducts = baseProducts.map((product: any) => {
          const updated = item.orderProducts.find((p: any) => p.productId === product.productId);
          return updated
            ? {
                ...product,
                price: updated.discountPrice,
                finalPriceIncTax: updated.price,
                isNewlyAddedProduct: true,
              }
            : product;
        });

        set({
          totalPrice: item.totalPrice,
          subTotalPrice: item.subTotalPrice,
          otTotalNoOtcPrice: item.otTotalNoOtcPrice,
          products: [...updatedProducts],
          isPriceUpdate: false,
        });
      },
      setIsPriceUpdate: async (value: boolean) => {
        set({
          isPriceUpdate: value,
        });
      },

      setIsLoader: (value: boolean) => {
        set({
          isLoader: value,
        });
      },

      setIsSaveModelOpen: (value: boolean) => {
        set({
          isSaveModelOpen: value,
        });
      },
      setIsRemoveAllPlan: (value: boolean) => {
        set({ isRemoveAllPlan: value });
      },
      setSelectedDevice: async (value: string) => {
        set({
          isSelectedDevice: value,
        });
      },

      getProductBasedOnProductId: async (id: number) => {
        return get().products.find((p: { productId: number }) => p.productId === id);
      },

      removeBroadbandProduct: () => {
        const allProducts = get().products;
        const filteredProducts = allProducts.filter(
          (p: { category: string }) => p.category.toLowerCase() === CATEGORY.TV.toLowerCase()
        );
        const bbProducts = allProducts.filter(
          (p: { category: string }) => p.category.toLowerCase() !== CATEGORY.TV.toLowerCase()
        );
        const bbRemovals = bbProducts.map((p: any) => ({
          code: p.sku,
          quantity: p.quantity || 1,
          action: 0,
          occurrenceNumber: p.occurrenceNumber ?? null,
        }));
        set(state => ({
          products: [...filteredProducts],
          CTItemsPayload: [...state.CTItemsPayload, ...bbRemovals],
        }));
      },

      setRemoveVoucherSkuList: async (skuList: string[]) => {
        set({
          removeVoucherSkuList: [...skuList],
        });
      },
      clearCTItemsPayload: () => {
        set({ CTItemsPayload: [] });
      },
      setIsHydrated: (value: boolean) => {
        set({ isHydrated: value });
      },

      clearCart: async () => {
        const clearedProducts = get().products;
        set({
          products: [],
          CTItemsPayload: [],
          totalPrice: 0,
          subTotalPrice: 0,
          otTotalNoOtcPrice: 0,
          isPriceUpdate: false,
          isLoader: false,
          isSelectedDevice: '',
          isSaveModelOpen: false,
          removeVoucherSkuList: [],
        });
        clearCompleteTvBundleQueryFlag(clearedProducts);
      },
    }),
    persistOptions
  )
);
